import { Establishment, createEmptyEstablishment } from "./establishment.types"

export interface SireneEstablishment {
  id: string
  name: string
  siret: string
  industryCode: string
  address: string
  x: number | null
  y: number | null
}

function cleanDigits(value: string) {
  return (value || "").replace(/\D/g, "")
}

function formatAddress(establishment: any) {
  if (establishment.adresse) return establishment.adresse

  const address = [
    establishment.numero_voie,
    establishment.type_voie,
    establishment.libelle_voie,
    establishment.code_postal,
    establishment.libelle_commune
  ]
    .filter(Boolean)
    .join(" ")

  return address || establishment.adresse || ""
}

function isActiveEstablishment(establishment: any) {
  const status = establishment.etat_administratif ?? establishment.etat_administratif_etablissement
  return !status || status === "A"
}

function getCoordinate(value: any) {
  if (value === null || value === undefined || value === "") return null
  const coordinate = Number(value)
  return Number.isNaN(coordinate) ? null : coordinate
}

async function geocodeAddress(address: string): Promise<{ x: number | null; y: number | null }> {
  if (!address) return { x: null, y: null }

  const response = await fetch(`https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(address)}&limit=1`)
  if (!response.ok) return { x: null, y: null }

  const data = await response.json()
  const coordinates = data.features?.[0]?.geometry?.coordinates
  return {
    x: getCoordinate(coordinates?.[0]),
    y: getCoordinate(coordinates?.[1])
  }
}

async function mapSireneEstablishment(item: any, accountName: string): Promise<SireneEstablishment | null> {
  const siret = item.siret
  if (!siret || !isActiveEstablishment(item)) return null
  const name = item.nom_commercial || item.enseigne || item.liste_enseignes?.[0] || accountName
  const address = formatAddress(item)
  let x = getCoordinate(item.longitude)
  let y = getCoordinate(item.latitude)

  if (x === null || y === null) {
    const geocoded = await geocodeAddress(address)
    x = x ?? geocoded.x
    y = y ?? geocoded.y
  }

  return {
    id: siret,
    name,
    siret,
    industryCode: item.activite_principale || "",
    address,
    x,
    y
  }
}

async function searchEnterprise(query: string) {
  const response = await fetch(
    "https://recherche-entreprises.api.gouv.fr/search" +
      `?q=${encodeURIComponent(query)}` +
      "&per_page=1" +
      "&limite_matching_etablissements=100"
  )

  if (!response.ok) throw new Error("Sirene search failed")

  return await response.json()
}

export async function fetchActiveEstablishmentsBySiren(
  siren: string,
  accountName: string
): Promise<SireneEstablishment[]> {
  const cleanSiren = cleanDigits(siren)
  const data = await searchEnterprise(cleanSiren)
  let result = data.results?.find((item) => item.siren === cleanSiren) ?? data.results?.[0]

  if (!result?.matching_etablissements?.length) {
    const searchName = result?.nom_raison_sociale || result?.nom_complet || accountName
    const dataByName = await searchEnterprise(searchName)
    result = dataByName.results?.find((item) => item.siren === cleanSiren) ?? result
  }

  const establishments = result?.matching_etablissements ?? []

  const mappedEstablishments = await Promise.all(
    establishments.map((item) => mapSireneEstablishment(item, result?.nom_complet || accountName))
  )

  return mappedEstablishments.filter((item): item is SireneEstablishment => item !== null)
}

export function createEstablishmentFromSirene(item: SireneEstablishment): Establishment {
  return createEmptyEstablishment({
    name: item.name,
    codeEstablishment: item.siret,
    industryCode: item.industryCode,
    address: item.address,
    x: item.x,
    y: item.y
  })
}
